import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LogOut, CreditCard, User, ChevronDown, Settings, Home } from 'lucide-react';

interface UserMenuProps {
  showLogout?: boolean;
}

export const UserMenu: React.FC<UserMenuProps> = ({ showLogout = true }) => {
  const { user, logout } = useAuth();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close the menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      await logout();
      // Redirect will happen automatically via the auth context
    } catch (error) {
      console.error('Error logging out:', error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        className="flex items-center bg-black/80 text-white rounded-full px-4 py-2 hover:bg-black/90 transition-colors"
      >
        <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center mr-2 overflow-hidden">
          {user?.profile_picture ? (
            <img
              src={user.profile_picture}
              alt="Profile"
              className="w-full h-full object-cover"
              crossOrigin="anonymous"
              referrerPolicy="no-referrer"
              onError={(e) => {
                // Fallback if image fails to load
                e.currentTarget.style.display = 'none';
                e.currentTarget.parentElement!.innerHTML = `<span className="text-gray-600 text-sm font-medium">${user?.name?.charAt(0) || '?'}</span>`;
              }}
            />
          ) : (
            <span className="text-gray-600 text-sm font-medium">
              {user?.name?.charAt(0) || '?'}
            </span>
          )}
        </div>
        <span className="text-sm font-medium">{user?.name}</span>
        <ChevronDown size={16} className="ml-2" />
      </button>

      {isMenuOpen && (
        <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 z-50">
          <div className="py-1" role="menu" aria-orientation="vertical" aria-labelledby="options-menu">
            <div className="px-4 py-2 text-sm text-gray-700 dark:text-gray-200 border-b border-gray-200 dark:border-gray-700">
              <div className="font-medium">{user?.name}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">{user?.email}</div>
              <div className="mt-1 text-xs font-medium text-indigo-600 dark:text-indigo-400">
                Credits: {user?.credits || 0}
              </div>
            </div>
            
            <Link
              to="/home"
              className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
              role="menuitem"
              onClick={() => setIsMenuOpen(false)}
            >
              <Home size={16} className="mr-2" />
              Home
            </Link>
            
            <Link
              to="/dashboard"
              className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
              role="menuitem"
              onClick={() => setIsMenuOpen(false)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              Fundraising Support
            </Link>
            
            <Link
              to="/profile"
              className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
              role="menuitem"
              onClick={() => setIsMenuOpen(false)}
            >
              <User size={16} className="mr-2" />
              Profile
            </Link>
            
            <Link
              to="/billing"
              className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
              role="menuitem"
              onClick={() => setIsMenuOpen(false)}
            >
              <CreditCard size={16} className="mr-2" />
              Billing & Subscriptions
            </Link>
            
            {/* <Link
              to="/settings"
              className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
              role="menuitem"
              onClick={() => setIsMenuOpen(false)}
            >
              <Settings size={16} className="mr-2" />
              Settings
            </Link> */}
            
            {showLogout && (
              <button
                onClick={handleLogout}
                disabled={isLoggingOut}
                className="flex w-full items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 border-t border-gray-200 dark:border-gray-700"
                role="menuitem"
              >
                {isLoggingOut ? (
                  <span className="animate-pulse">Logging out...</span>
                ) : (
                  <>
                    <LogOut size={16} className="mr-2" />
                    Logout
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
